import type { MediaItem } from "@gooner/shared";

/**
 * What a leader region is playing right now, for the regions synced to it.
 *
 * A follower needs three things and no more: which clip, when it started, and the
 * element it is playing in. The element is what lets a follower align to the FRAME
 * rather than to the wall clock — two videos told to start at the same instant drift by
 * however long each took to decode its first frame, which is visible as soon as the two
 * tiles sit side by side.
 */
export interface SyncState {
  item: MediaItem;
  /**
   * The leader's RESOLVED source url, passed on verbatim rather than re-derived.
   *
   * This is the whole "no duplicate requests" claim. Media urls carry a rendition
   * suffix chosen from the tile's pixel size, so a follower that recomputed its own
   * would ask for `/r/480` beside the leader's `/r/720` whenever the two tiles differ
   * in size — a different url, a second download, and the feature paying for itself
   * twice. Reusing the exact string guarantees the browser cache serves it: the media
   * route sends `immutable, max-age=31536000` against a content-addressed path.
   *
   * The cost is a follower on a big tile showing the leader's smaller rendition. That
   * is the correct trade — it is the same picture either way, and bandwidth is this
   * app's actual bottleneck.
   */
  src: string;
  /** When the leader began this clip. Followers seek to match. */
  startedAt: number;
  /** The leader's live element, so a follower can read `currentTime` directly. */
  video: HTMLVideoElement | null;
}

type Listener<T> = (s: T | null) => void;

/**
 * A per-view channel from leader regions to their followers.
 *
 * Deliberately NOT React state or context. The leader publishes on every clip change and
 * a follower re-renders only when its own leader moves; routing this through a context
 * would re-render every region on the wall each time any one of them advanced, which on a
 * sixteen-tile wall is sixteen times the work for one tile's news.
 *
 * Owned by DisplayApp and passed down, rather than a module singleton: two screens open
 * in one browser are two walls, and a module-level bus would cross them — region ids are
 * only unique within a view.
 *
 * Generic over the state (2026-09-19): a Goon Room's members follow the HOST's regions
 * over the socket, and that channel — region id → what it plays, seeded on mount,
 * updated on change — is this exact shape with a RoomRegionState in it.
 */
export class SyncBus<T = SyncState> {
  private readonly state = new Map<string, T>();
  private readonly listeners = new Map<string, Set<Listener<T>>>();
  private readonly everyListeners = new Set<(leaderId: string, next: T | null) => void>();

  /** A leader announces what it is playing. */
  publish(leaderId: string, next: T): void {
    this.state.set(leaderId, next);
    const ls = this.listeners.get(leaderId);
    if (ls) for (const fn of ls) fn(next);
    for (const fn of this.everyListeners) fn(leaderId, next);
  }

  /**
   * A leader has gone — unmounted, or stopped being a leader.
   *
   * Followers are told explicitly rather than left holding the last frame forever: a tile
   * frozen on a clip that is no longer playing anywhere looks like a crash, and "nothing
   * to follow" is a state a follower can render honestly.
   */
  clear(leaderId: string): void {
    this.state.delete(leaderId);
    const ls = this.listeners.get(leaderId);
    if (ls) for (const fn of ls) fn(null);
    for (const fn of this.everyListeners) fn(leaderId, null);
  }

  /** Every publish and clear, whatever the id — a room's HOST forwards its whole board
   *  this way (a scroll's page ids come and go; per-id subscriptions cannot follow them). */
  subscribeAll(fn: (leaderId: string, next: T | null) => void): () => void {
    this.everyListeners.add(fn);
    return () => {
      this.everyListeners.delete(fn);
    };
  }

  /** Current value, for a follower mounting mid-clip rather than at a change. */
  get(leaderId: string): T | null {
    return this.state.get(leaderId) ?? null;
  }

  /** Every id with a state — a room re-point clears them all. */
  ids(): string[] {
    return [...this.state.keys()];
  }

  subscribe(leaderId: string, fn: Listener<T>): () => void {
    let set = this.listeners.get(leaderId);
    if (!set) {
      set = new Set();
      this.listeners.set(leaderId, set);
    }
    set.add(fn);
    return () => {
      set.delete(fn);
      // Drop the empty set: a wall whose layout changes often would otherwise accumulate
      // one entry per region id it has ever had.
      if (set.size === 0) this.listeners.delete(leaderId);
    };
  }
}
